import React, { useState, useEffect } from 'react';
import { DetailedCacheReport } from './cache/DetailedCacheReport';
import { GlobalCacheReport } from './cache/GlobalCacheReport';
import axios from 'axios';
import { 
  ArrowPathIcon, 
  TrashIcon, 
  XCircleIcon,
  DocumentTextIcon,
  ChartBarIcon,
  GlobeAltIcon,
  ArrowLeftIcon,
} from '@heroicons/react/24/outline';

// Endereço do serviço Python de monitoramento
const CACHE_SERVICE_URL = 'http://localhost:5500';

// Componente para exibir logs do cache
const LogsScreen = ({ onDownloadLogs }) => {
  const [cacheLogs, setCacheLogs] = useState([]);
  const [logsLoading, setLogsLoading] = useState(false);

  // Função para buscar logs
  const fetchCacheLogs = async () => {
    setLogsLoading(true);
    try {
      const response = await axios.get(`${CACHE_SERVICE_URL}/logs?max=50`);
      if (response.data && response.data.status === 'success') {
        setCacheLogs(response.data.logs);
      } else {
        throw new Error('Erro ao buscar logs');
      }
    } catch (err) {
      console.error('Erro ao buscar logs:', err);
    } finally {
      setLogsLoading(false);
    }
  };

  // Carregar logs quando o componente montar
  useEffect(() => {
    fetchCacheLogs();
  }, []);

  return (
    <div className="bg-white rounded-lg">
      <h3 className="text-lg font-semibold mb-4">Logs Estratégicos do Cache</h3>
      
      <div className="overflow-y-auto">
        {logsLoading ? (
          <div className="flex justify-center py-8">
            <div className="w-8 h-8 border-t-2 border-b-2 border-blue-500 rounded-full animate-spin"></div>
          </div>
        ) : cacheLogs.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-2 text-left">Timestamp</th>
                  <th className="px-4 py-2 text-left">Salão ID</th>
                  <th className="px-4 py-2 text-left">Operação</th>
                  <th className="px-4 py-2 text-left">Detalhes</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {cacheLogs.map((log, index) => (
                  <tr key={index} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                    <td className="px-4 py-2 whitespace-nowrap">
                      {new Date(log.timestamp).toLocaleString('pt-BR')}
                    </td>
                    <td className="px-4 py-2 whitespace-nowrap">
                      {log.salon_id || log.type || '-'}
                    </td>
                    <td className="px-4 py-2 whitespace-nowrap">
                      {log.operation || (log.type === 'counters' ? 'Contadores' : '-')}
                    </td>
                    <td className="px-4 py-2">
                      {log.type === 'counters' ? (
                        <span>
                          Hits: {log.hit_count}, Misses: {log.miss_count}, 
                          Taxa: {(log.hit_rate * 100).toFixed(1)}%
                        </span>
                      ) : (
                        <span>
                          {log.details && typeof log.details === 'object'
                            ? JSON.stringify(log.details)
                            : log.details || '-'}
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="text-center py-8 text-gray-500">
            Nenhum log disponível
          </div>
        )}
      </div>
      
      <div className="mt-6 flex justify-end">
        <button 
          onClick={onDownloadLogs}
          className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
        >
          Baixar Logs
        </button>
      </div>
    </div>
  );
};

/**
 * Componente para monitorar o cache da aplicação
 */
const CacheMonitor = ({ salonId, salonEmail, isAdmin }) => {
  const [cacheStats, setCacheStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [serviceStatus, setServiceStatus] = useState(null);
  const [showDetailedReport, setShowDetailedReport] = useState(false);
  const [showGlobalReport, setShowGlobalReport] = useState(false);
  const [clearingCache, setClearingCache] = useState(false);
  const [invalidationPath, setInvalidationPath] = useState('');
  const [invalidating, setInvalidating] = useState(false);
  const [operationStatus, setOperationStatus] = useState(null);
  const [showLogsModal, setShowLogsModal] = useState(false);
  
  // Estado para controlar qual tela está sendo exibida
  const [activeScreen, setActiveScreen] = useState('main');
  const [isMobile, setIsMobile] = useState(false);

  // Verificar se é dispositivo móvel baseado no tamanho da tela
  useEffect(() => {
    const checkIfMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    checkIfMobile();
    window.addEventListener('resize', checkIfMobile);
    
    return () => {
      window.removeEventListener('resize', checkIfMobile);
    };
  }, []);

  // Verificar status do serviço Python
  useEffect(() => {
    const checkServiceStatus = async () => {
      try {
        const response = await axios.get(`${CACHE_SERVICE_URL}/status`);
        setServiceStatus(response.data);
        return response.data.status === 'online';
      } catch (err) {
        console.error('Erro ao verificar o status do serviço de cache:', err);
        setServiceStatus({ status: 'offline', error: err.message });
        return false;
      }
    };

    const fetchCacheStats = async () => {
      setLoading(true);
      try {
        const isServiceOnline = await checkServiceStatus();
        
        if (!isServiceOnline) {
          throw new Error('Serviço de cache offline');
        }
        
        const response = await axios.get(`${CACHE_SERVICE_URL}/report/${salonId}`);
        setCacheStats(response.data);
        setError(null);
      } catch (err) {
        console.error('Erro ao carregar estatísticas de cache:', err);
        setError(err.message || 'Erro ao carregar estatísticas de cache');
        setCacheStats(null);
      } finally {
        setLoading(false);
      }
    };

    fetchCacheStats();
    
    // Atualizar status do serviço a cada 5 minutos
    const intervalId = setInterval(() => {
      checkServiceStatus();
    }, 300000);

    return () => clearInterval(intervalId);
  }, [salonId]);

  // Função para limpar o cache
  const handleClearCache = async () => {
    setClearingCache(true);
    setOperationStatus(null);
    
    try {
      const response = await axios.post(`${CACHE_SERVICE_URL}/clear/${salonId}`);
      setOperationStatus({
        type: 'success',
        message: response.data.message || 'Cache limpo com sucesso'
      });
      
      // Atualizar estatísticas depois de limpar
      setTimeout(async () => {
        try {
          const response = await axios.get(`${CACHE_SERVICE_URL}/report/${salonId}`);
          setCacheStats(response.data);
        } catch (err) {
          console.error('Erro ao atualizar estatísticas:', err);
        } finally {
          setClearingCache(false);
        }
      }, 1500);
    } catch (err) {
      console.error('Erro ao limpar cache:', err);
      setOperationStatus({
        type: 'error',
        message: err.response?.data?.message || 'Erro ao limpar cache'
      });
      setClearingCache(false);
    }
  };

  // Função para invalidar uma entrada específica
  const handleInvalidateEntry = async () => {
    if (!invalidationPath.trim()) {
      setOperationStatus({
        type: 'error',
        message: 'É necessário fornecer um caminho para invalidar'
      });
      return;
    }
    
    setInvalidating(true);
    setOperationStatus(null);
    
    try {
      const response = await axios.post(`${CACHE_SERVICE_URL}/invalidate/${salonId}`, {
        path: invalidationPath.trim()
      });
      
      setOperationStatus({
        type: 'success',
        message: response.data.message || 'Cache invalidado com sucesso'
      });
      
      // Limpar campo de entrada
      setInvalidationPath('');
      
      // Atualizar estatísticas depois de invalidar
      setTimeout(async () => {
        try {
          const response = await axios.get(`${CACHE_SERVICE_URL}/report/${salonId}`);
          setCacheStats(response.data);
        } catch (err) {
          console.error('Erro ao atualizar estatísticas:', err);
        } finally {
          setInvalidating(false);
        }
      }, 1500);
    } catch (err) {
      console.error('Erro ao invalidar cache:', err);
      setOperationStatus({
        type: 'error',
        message: err.response?.data?.message || 'Erro ao invalidar cache'
      });
      setInvalidating(false);
    }
  };

  // Função para mostrar modal de logs
  const handleShowLogs = () => {
    setShowLogsModal(true);
  };

  // Função para baixar logs
  const handleDownloadLogs = () => {
    window.open(`${CACHE_SERVICE_URL}/logs/download`, '_blank');
  };

  // Opções do menu de cache
  const cacheOptions = [
    {
      id: 'detailed',
      name: 'Relatório Detalhado',
      description: 'Visualizar estatísticas detalhadas de desempenho do cache',
      icon: ChartBarIcon,
      component: () => <DetailedCacheReport salonId={salonId} />,
      visibleFor: 'admin'
    },
    {
      id: 'global',
      name: 'Relatório Global',
      description: 'Visualizar estatísticas globais de todos os salões',
      icon: GlobeAltIcon,
      component: () => <GlobalCacheReport />,
      visibleFor: 'admin'
    },
    {
      id: 'logs',
      name: 'Logs de Cache',
      description: 'Visualizar e baixar logs do sistema de cache',
      icon: DocumentTextIcon,
      component: () => <LogsScreen onDownloadLogs={handleDownloadLogs} />,
      visibleFor: 'admin'
    }
  ];

  // Renderização do cabeçalho mobile
  const renderMobileHeader = (title = 'Monitor de Cache') => (
    <div className="sticky top-0 bg-white z-10 border-b border-gray-200 mb-4">
      <div className="flex items-center p-4">
        {activeScreen !== 'main' && (
          <button 
            onClick={() => setActiveScreen('main')}
            className="mr-4"
          >
            <ArrowLeftIcon className="h-6 w-6 text-black" />
          </button>
        )}
        <h1 className="text-xl font-semibold">{title}</h1>
      </div>
    </div>
  );

  // Renderizar a tela principal para mobile
  const renderMobileMainScreen = () => (
    <div className="bg-gray-50 min-h-screen pb-16">
      {renderMobileHeader()}
      
      {/* Status do serviço */}
      <div className={`mx-4 mb-4 p-3 rounded ${serviceStatus?.status === 'online' ? 'bg-green-100' : 'bg-yellow-100'}`}>
        <div className="flex items-center">
          <div className={`w-3 h-3 rounded-full mr-2 ${serviceStatus?.status === 'online' ? 'bg-green-500' : 'bg-yellow-500'}`}></div>
          <p className="text-sm">
            {serviceStatus?.status === 'online' 
              ? 'Serviço de monitoramento online' 
              : 'Serviço de monitoramento com problemas'}
          </p>
        </div>
      </div>
      
      {/* Estatísticas Básicas */}
      <div className="mx-4 grid grid-cols-2 gap-3 mb-6">
        <div className="p-3 bg-blue-50 rounded-lg">
          <div className="text-sm text-blue-600">Taxa de Acerto</div>
          <div className="text-xl font-bold">{((latestReport?.hitRate || 0) * 100).toFixed(1)}%</div>
        </div>
        
        <div className="p-3 bg-green-50 rounded-lg">
          <div className="text-sm text-green-600">Requisições</div>
          <div className="text-xl font-bold">{latestReport?.totalRequests || 0}</div>
        </div>
        
        <div className="p-3 bg-purple-50 rounded-lg">
          <div className="text-sm text-purple-600">Tamanho</div>
          <div className="text-xl font-bold">{latestReport?.size || 0} KB</div>
        </div>
        
        <div className="p-3 bg-amber-50 rounded-lg">
          <div className="text-sm text-amber-600">Acertos Obsoletos</div>
          <div className="text-xl font-bold">{latestReport?.staleHits || 0}</div>
        </div>
      </div>
      
      {/* Ações do cache */}
      <div className="mx-4 bg-white rounded-lg p-4 mb-6">
        <h3 className="text-md font-medium mb-3">Ações</h3>
        
        <div className="space-y-3">
          <button
            onClick={handleClearCache}
            disabled={clearingCache}
            className={`w-full flex items-center justify-center px-4 py-2 rounded text-white ${clearingCache ? 'bg-gray-400' : 'bg-red-500'}`}
          >
            <TrashIcon className="h-4 w-4 mr-2" />
            {clearingCache ? 'Limpando...' : 'Limpar Cache'}
          </button>
          
          <div className="flex flex-col">
            <label className="text-sm mb-1 text-gray-600">Invalidar Caminho Específico:</label>
            <div className="flex">
              <input
                type="text"
                value={invalidationPath}
                onChange={(e) => setInvalidationPath(e.target.value)}
                placeholder="ex: /appointments"
                className="flex-1 px-3 py-2 border rounded-l text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              <button
                onClick={handleInvalidateEntry}
                disabled={invalidating || !invalidationPath.trim()}
                className={`px-4 py-2 rounded-r text-white ${invalidating || !invalidationPath.trim() ? 'bg-gray-400' : 'bg-orange-500'}`}
              >
                <XCircleIcon className="h-5 w-5" />
              </button>
            </div>
          </div>
        </div>
      </div>
      
      {/* Lista de opções */}
      <div className="mx-4">
        <h3 className="text-sm font-semibold text-gray-500 mb-2">Relatórios e Análises</h3>
        <div className="bg-white rounded-lg">
          {cacheOptions.filter(option => option.visibleFor === 'all' || (option.visibleFor === 'admin' && isAdmin)).map((option, index, array) => (
            <button
              key={option.id}
              onClick={() => setActiveScreen(option.id)}
              className={`w-full px-4 py-3 flex items-center justify-between text-left ${
                index !== array.length - 1 ? 'border-b border-gray-100' : ''
              }`}
            >
              <div className="flex items-center">
                <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center">
                  <option.icon className="h-4 w-4 text-blue-600" />
                </div>
                <div className="ml-3">
                  <span className="text-base font-medium">{option.name}</span>
                  <p className="text-xs text-gray-500">{option.description}</p>
                </div>
              </div>
              <ArrowLeftIcon className="h-4 w-4 text-gray-400 transform rotate-180" />
            </button>
          ))}
        </div>
      </div>
      
      {/* Mensagem de status da operação */}
      {operationStatus && (
        <div className={`mx-4 mt-4 p-3 rounded text-sm ${operationStatus.type === 'success' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
          {operationStatus.message}
        </div>
      )}
    </div>
  );

  // Renderizar a tela principal para desktop
  const renderDesktopMainScreen = () => {
    // Se temos estatísticas válidas, vamos exibi-las
    const reports = cacheStats?.reports || [];
    const latestReport = reports[0] || {};
    
    // Calcular métricas básicas
    const cacheSize = latestReport.size || 0;
    const hitRate = latestReport.hitRate || 0;
    const effectiveHitRate = latestReport.effectiveHitRate || 0;
    const totalRequests = latestReport.totalRequests || 0;
    const hits = latestReport.hits || 0;
    const misses = latestReport.misses || 0;
    const staleHits = latestReport.staleHits || 0;
  
    return (
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <h2 className="text-2xl font-bold text-gray-800 mb-2">Monitor de Cache</h2>
          <button 
            onClick={async () => {
              setLoading(true);
              try {
                const response = await axios.get(`${CACHE_SERVICE_URL}/report/${salonId}`);
                setCacheStats(response.data);
              } catch (err) {
                console.error("Erro ao atualizar estatísticas:", err);
              } finally {
                setLoading(false);
              }
            }}
            className="px-3 py-1 flex items-center text-sm text-gray-600 hover:text-gray-900"
          >
            <ArrowPathIcon className="h-4 w-4 mr-1" /> Atualizar
          </button>
        </div>
        
        {/* Status do serviço */}
        <div className={`p-3 rounded ${serviceStatus?.status === 'online' ? 'bg-green-100' : 'bg-yellow-100'}`}>
          <div className="flex items-center">
            <div className={`w-3 h-3 rounded-full mr-2 ${serviceStatus?.status === 'online' ? 'bg-green-500' : 'bg-yellow-500'}`}></div>
            <p>
              {serviceStatus?.status === 'online' 
                ? 'Serviço de monitoramento online' 
                : 'Serviço de monitoramento com problemas'}
            </p>
          </div>
        </div>
        
        {/* Estatísticas Básicas */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <div className="p-4 bg-blue-50 rounded-lg shadow-sm">
            <div className="text-sm text-blue-600">Taxa de Acerto</div>
            <div className="text-2xl font-bold">{(hitRate * 100).toFixed(1)}%</div>
            <div className="text-xs text-blue-500">Efetiva: {(effectiveHitRate * 100).toFixed(1)}%</div>
          </div>
          
          <div className="p-4 bg-green-50 rounded-lg shadow-sm">
            <div className="text-sm text-green-600">Requisições</div>
            <div className="text-2xl font-bold">{totalRequests}</div>
            <div className="text-xs text-green-500">{hits} acertos / {misses} falhas</div>
          </div>
          
          <div className="p-4 bg-purple-50 rounded-lg shadow-sm">
            <div className="text-sm text-purple-600">Tamanho do Cache</div>
            <div className="text-2xl font-bold">{cacheSize} KB</div>
            <div className="text-xs text-purple-500">Estimado</div>
          </div>
          
          <div className="p-4 bg-amber-50 rounded-lg shadow-sm">
            <div className="text-sm text-amber-600">Acertos Obsoletos</div>
            <div className="text-2xl font-bold">{staleHits}</div>
            <div className="text-xs text-amber-500">{staleHits > 0 ? `${((staleHits / hits) * 100).toFixed(1)}% dos acertos` : 'Nenhum'}</div>
          </div>
        </div>
        
        {/* Ações */}
        <div className="p-6 bg-white rounded-lg shadow-sm border border-gray-100">
          <h3 className="text-lg font-medium mb-4">Ações do Cache</h3>
          
          <div className="flex flex-col sm:flex-row gap-4 mb-4">
            <button
              onClick={handleClearCache}
              disabled={clearingCache}
              className={`px-4 py-2 rounded text-white flex items-center justify-center ${clearingCache ? 'bg-gray-400' : 'bg-red-500 hover:bg-red-600'}`}
            >
              <TrashIcon className="h-5 w-5 mr-2" />
              {clearingCache ? 'Limpando...' : 'Limpar Cache'}
            </button>
            
            <div className="flex-1 flex">
              <input
                type="text"
                value={invalidationPath}
                onChange={(e) => setInvalidationPath(e.target.value)}
                placeholder="Caminho para invalidar (ex: /appointments)"
                className="flex-1 px-3 py-2 border rounded-l focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              <button
                onClick={handleInvalidateEntry}
                disabled={invalidating || !invalidationPath.trim()}
                className={`px-4 py-2 rounded-r text-white ${invalidating || !invalidationPath.trim() ? 'bg-gray-400' : 'bg-orange-500 hover:bg-orange-600'} flex items-center`}
              >
                <XCircleIcon className="h-5 w-5 mr-1" />
                {invalidating ? 'Invalidando...' : 'Invalidar'}
              </button>
            </div>
          </div>
          
          {/* Mensagem de status da operação */}
          {operationStatus && (
            <div className={`p-3 rounded ${operationStatus.type === 'success' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
              {operationStatus.message}
            </div>
          )}
        </div>
        
        {/* Opções de relatórios */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {cacheOptions
            .filter(option => option.visibleFor === 'all' || (option.visibleFor === 'admin' && isAdmin))
            .map((option) => (
              <div 
                key={option.id} 
                className="bg-white p-0 rounded-lg shadow-sm border border-gray-100 overflow-hidden"
              >
                <button
                  onClick={() => setActiveScreen(option.id)}
                  className="w-full p-4 text-left flex items-center hover:bg-gray-50 transition-colors"
                >
                  <div className="flex-shrink-0 h-10 w-10 rounded-full bg-blue-100 flex items-center justify-center mr-4">
                    <option.icon className="h-6 w-6 text-blue-600" />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-lg font-medium text-gray-800">{option.name}</h3>
                    <p className="text-sm text-gray-500">{option.description}</p>
                  </div>
                  <ArrowLeftIcon className="h-5 w-5 text-gray-400 transform rotate-180" />
                </button>
              </div>
            ))}
        </div>
      </div>
    );
  };

  // Função para renderizar o componente da tela selecionada
  const renderScreen = () => {
    if (loading) {
      return (
        <div className="p-4 border rounded-lg shadow bg-white">
          <h2 className="text-xl font-semibold mb-4">Monitor de Cache</h2>
          <div className="flex items-center justify-center py-4">
            <div className="w-8 h-8 border-t-2 border-b-2 border-blue-500 rounded-full animate-spin"></div>
            <span className="ml-2">Carregando estatísticas de cache...</span>
          </div>
        </div>
      );
    }

    // Exibir erro de conexão com o serviço
    if (error || serviceStatus?.status === 'offline') {
      return (
        <div className="p-4 border rounded-lg shadow bg-white">
          <h2 className="text-xl font-semibold mb-4">Monitor de Cache</h2>
          <div className="bg-red-100 border-l-4 border-red-500 text-red-700 p-4 mb-4">
            <p className="font-bold">Serviço de Cache Indisponível</p>
            <p>{error || 'Não foi possível conectar ao serviço de monitoramento de cache'}</p>
          </div>
          <div className="flex justify-end">
            <button
              onClick={() => window.location.reload()}
              className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
            >
              Tentar Novamente
            </button>
          </div>
        </div>
      );
    }

    if (activeScreen === 'main') {
      return isMobile ? renderMobileMainScreen() : renderDesktopMainScreen();
    }
    
    const selectedOption = cacheOptions.find(option => option.id === activeScreen);
    
    if (!selectedOption || !selectedOption.component) {
      return (
        <div className={`text-center p-8 ${isMobile ? 'min-h-screen bg-white' : ''}`}>
          {isMobile && renderMobileHeader('Voltar')}
          
          <h3 className="text-lg font-medium text-gray-800 mb-2">Recurso em Desenvolvimento</h3>
          <p className="text-gray-500 mb-4">Esta funcionalidade será implementada em breve.</p>
          {!isMobile && (
            <button onClick={() => setActiveScreen('main')} className="px-4 py-2 bg-blue-500 text-white rounded">
              Voltar
            </button>
          )}
        </div>
      );
    }
    
    const ScreenComponent = selectedOption.component;
    return (
      <div className={isMobile ? 'min-h-screen bg-white' : ''}>
        <div className={`${isMobile ? 'sticky top-0 bg-white z-10 border-b border-gray-200' : 'mb-4'}`}>
          {isMobile ? (
            <div className="flex items-center p-4">
              <button 
                onClick={() => setActiveScreen('main')}
                className="mr-4"
              >
                <ArrowLeftIcon className="h-6 w-6 text-black" />
              </button>
              <h1 className="text-xl font-semibold">{selectedOption.name}</h1>
            </div>
          ) : (
            <>
              <button onClick={() => setActiveScreen('main')} className="px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg flex items-center mb-4">
                <ArrowLeftIcon className="h-4 w-4 mr-1" /> Voltar para o Monitor
              </button>
              <h2 className="text-2xl font-bold text-gray-800 mb-4">{selectedOption.name}</h2>
            </>
          )}
        </div>
        
        <ScreenComponent />
      </div>
    );
  };

  // Obter dados do último relatório
  const reports = cacheStats?.reports || [];
  const latestReport = reports[0] || {};

  return (
    <div className="w-full">
      {renderScreen()}
    </div>
  );
};

export default CacheMonitor; 